import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {AlertService} from '../../sdk/service/alert.service';
import {BehaviorSubject, finalize, Subject} from 'rxjs';
import {
    BusinessType,
    MerchantIntegratorDetails,
    PublicService,
    Response,
    Signup,
    SignupResponse
} from 'web-angular-client';
import {MenuItem} from 'primeng/api';
import {SignupDetail} from './model/signup-detail';
import {KycDetail} from '../../sdk/interface/model/kyc-detail';
import {MerchantIntegratorSignupDetails} from './model/merchant-integrator-signup-details';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {AddressDetail} from '../../sdk/interface/model/address-detail';
import {ROUTES} from '../../app.routes';
import {HttpStatusCode} from '@angular/common/http';

import {BankDetails} from '../../sdk/interface/model/banking-details';
import {EnvironmentService} from '../../sdk/service/environment.service';
import {ReCaptchaV3Service} from 'ng-recaptcha-2';
import {PasswordGenerator} from '../../sdk/util/password-generator';
import {SignupMode} from './signup-mode/signup-mode.enum';
import {SignupModeService} from './signup-mode/signup-mode.service';
import ResponseEnum = Response.ResponseCodeEnum;

@Component({
    selector: 'app-signup',
    templateUrl: './sign-up.component.html',
    styleUrls: ['./sign-up.component.scss'],
    standalone: false
})
export class SignUpComponent implements OnInit, OnDestroy
{
    signupDetailSubject: Subject<SignupDetail>;
    integratorSignupDetails: MerchantIntegratorSignupDetails;
    merchantIntegratorDetails: MerchantIntegratorDetails;
    currentStep: number;
    signupDetail: SignupDetail;
    kycDetail: KycDetail;
    addressDetail: AddressDetail;
    bankingDetails: BankDetails;
    signupForm: Signup;
    directorNameSubject: Subject<string>;
    directorSurnameSubject: Subject<string>;
    businessTypeSubject: BehaviorSubject<BusinessType>;
    screenWidth: number;
    loaderText = 'Loading ...';
    signupSteps: MenuItem[];
    routes = ROUTES;
    isWalkthrough = false;
    lastStepIndex: number;
    mode: SignupMode = SignupMode.FULL;
    protected readonly SignupMode = SignupMode;

    constructor(
        protected router: Router,
        protected alertService: AlertService,
        protected route: ActivatedRoute,
        protected publicService: PublicService,
        protected loaderService: NgxUiLoaderService,
        protected recaptchaV3Service: ReCaptchaV3Service,
        protected environmentService: EnvironmentService,
        protected signupModeService: SignupModeService)
    {
        this.currentStep = 0;
        this.signupDetailSubject = new Subject();
        this.directorNameSubject = new Subject();
        this.directorSurnameSubject = new Subject();
        this.businessTypeSubject = new BehaviorSubject<BusinessType>(null);
        this.recaptchaV3Service = recaptchaV3Service;
    }

    get walkThroughLink()
    {
        return this.environmentService.walkThroughLink;
    }

    get logoSecondary(): string
    {
        return this.environmentService.logoSecondary;
    }

    get gatewayLogoSrc(): string | undefined
    {
        return this.environmentService.gatewayLogoSrc;
    }

    getIsIntegratorSignup(): boolean
    {
        return this.integratorSignupDetails.isIntegratorSignup;
    }

    getIsMerchantSelfServiceEnabled(): boolean
    {
        return !!this.merchantIntegratorDetails?.merchantSelfServiceEnabled;
    }

    /**
     * Whether the walkthrough and FAQ help bubble is hidden: on the walkthrough itself, in TRIMMED mode, or when
     * merchant self-service is off.
     */
    isHelpBubbleHidden(): boolean
    {
        return this.isWalkthrough || this.mode === SignupMode.TRIMMED || !this.getIsMerchantSelfServiceEnabled();
    }

    ngOnInit()
    {
        this.screenWidth = window.innerWidth;
        this.mode = this.signupModeService.resolveFromRoute(this.route.snapshot);
        this.updateSignupSteps();
        this.retrieveIntegratorSignupDetails();
        this.loadPrefilledSignupDetails();
        this.isWalkthrough = this.route.data['value']['walkthrough'] ?? false;
        this.lastStepIndex = this.route.data['value']['lastStepIndex'];
    }

    ngOnDestroy(): void
    {
        this.signupDetailSubject.unsubscribe();
        this.directorNameSubject.unsubscribe();
        this.directorSurnameSubject.unsubscribe();
        this.businessTypeSubject.unsubscribe();
    }

    processNext(event: any)
    {
        if (this.mode === SignupMode.TRIMMED)
        {
            this.submitTrimmedSignupForm();
            return;
        }

        ++this.currentStep;
        this.updateSignupSteps();
        console.log('next step', this.currentStep);
    }

    processPrevious()
    {
        --this.currentStep;
        this.updateSignupSteps();
        console.log('previous step', this.currentStep);
    }

    @HostListener('window:resize')
    onResize()
    {
        this.screenWidth = window.innerWidth;
        this.updateSignupSteps();
    }

    public isMobile(): boolean
    {
        return this.screenWidth < 756;
    }

    public getCurrentSignupStep()
    {
        return this.currentStep;
    }

    updateSignupSteps()
    {
        if (this.mode === SignupMode.TRIMMED)
        {
            this.signupSteps = [];
            return;
        }

        this.signupSteps = [
            {label: (this.currentStep !== 0 && this.isMobile()) ? '' : 'User and Business Profile'},
            {label: (this.currentStep !== 1 && this.isMobile()) ? '' : 'Business Details (Required for KYC)'},
            {label: (this.currentStep !== 2 && this.isMobile()) ? '' : 'Banking Details'},
            {label: (this.currentStep !== 3 && this.isMobile()) ? '' : 'Address Details'}
        ];
    }

    public onSignupDetailSubmission(signupDetail: SignupDetail)
    {
        this.signupDetail = signupDetail;
        this.directorNameSubject.next(this.signupDetail.name);
        this.directorSurnameSubject.next(this.signupDetail.surname);
        this.businessTypeSubject.next(this.signupDetail.businessType);
    }

    public onKycDetailSubmission(kycDetail: KycDetail)
    {
        this.kycDetail = kycDetail;
    }

    /**
     * This is the last step (Step 4) in the signup process and will kick of the sign-up call to the engine.
     *
     * @param addressDetail
     *      the address details filled in by the user
     */
    public onAddressDetailSubmission(addressDetail: AddressDetail)
    {
        this.addressDetail = addressDetail;

        this.recaptchaV3Service.execute('importantAction').subscribe({
            next: (token: string) =>
            {
                this.kycDetail.token = token;
                this.submitSignupForm();
            }, error: () =>
            {
                this.alertService.error('Recaptcha could not be verified.');
            }
        });
    }

    public onBankingDetailsSubmission(bankingDetails: BankDetails)
    {
        this.bankingDetails = bankingDetails;
    }

    getDirectorNameSubject()
    {
        return this.directorNameSubject;
    }

    getDirectorSurnameSubject()
    {
        return this.directorSurnameSubject;
    }

    getBusinessTypeSubject()
    {
        return this.businessTypeSubject;
    }

    getIntegratorPrefix()
    {
        return this.integratorSignupDetails.integratorPrefix;
    }

    protected convertToSignupForSubmission(
        signupDetail: SignupDetail,
        kycDetail: KycDetail | undefined,
        addressDetail: AddressDetail | undefined,
        bankingDetails: BankDetails | undefined,
        recaptchaToken?: string): Signup
    {
        return {
            businessName: signupDetail.businessName,
            businessType: signupDetail.businessType,
            customRefEnabled: false,
            name: signupDetail.name,
            surname: signupDetail.surname,
            email: signupDetail.email,
            mobileNumber: signupDetail.mobileNumber,
            password: signupDetail.password,
            dateTermsAccepted: signupDetail.dateTermsAccepted.toISOString(),
            clientBase: signupDetail.clientBase,
            averageTransactionValue: signupDetail.averageTransactionValue,
            registrationNumber: kycDetail?.registrationNumber,
            marketingConsent: signupDetail.marketingConsent,
            directorName: kycDetail?.directorName,
            directorSurname: kycDetail?.directorSurname,
            directorIdNumber: kycDetail?.directorIdNumber,
            fspNumber: kycDetail?.fspNumber,
            natureOfBusiness: kycDetail?.natureOfBusiness ?? signupDetail.natureOfBusiness,
            overview: kycDetail?.overview,
            websiteUrl: kycDetail?.websiteUrl,
            recaptchaToken: recaptchaToken ?? kycDetail?.token,
            address1: addressDetail?.address1,
            address2: addressDetail?.address2,
            city: addressDetail?.city,
            province: addressDetail?.province,
            postalCode: addressDetail?.postalCode,
            billingEmail: addressDetail?.billingEmailAddress,
            bankName: bankingDetails?.bankName,
            bankAccountNumber: bankingDetails?.bankAccountNumber,
            bankBranch: bankingDetails?.bankBranch,
            businessDisplayName: signupDetail.businessDisplayName,
            externalIdentifier: this.integratorSignupDetails.merchantIdentifier,
            integratorPrefix: this.integratorSignupDetails.integratorPrefix,
            integratorUuid: this.integratorSignupDetails.integratorUuid,
            integratorSignup: this.integratorSignupDetails.isIntegratorSignup
        } as Signup;
    }

    private submitTrimmedSignupForm()
    {
        this.recaptchaV3Service.execute('importantAction').subscribe({
            next: (token: string) =>
            {
                this.startSubmitSignupLoader('Sending your verification email…');
                this.signupForm = this.convertToSignupForSubmission(
                    this.signupDetail,
                    undefined,
                    undefined,
                    undefined,
                    token);

                this.publicService.signupEcommerce(this.signupForm).subscribe({
                    next: () =>
                    {
                        this.stopSubmitSignupLoader();
                        this.showCheckEmailPopup();
                        this.navigateToLogin();
                    },
                    error: (error) =>
                    {
                        this.stopSubmitSignupLoader();
                        this.alertService.error(error);
                    }
                });
            },
            error: () =>
            {
                this.alertService.error('Recaptcha could not be verified.');
            }
        });
    }

    private submitSignupForm()
    {
        this.startSubmitSignupLoader(
            'Please note that we will be starting an automated verification process and this may take' +
            ' a few minutes. Please do not refresh or close your browser!');
        this.signupForm = this.convertToSignupForSubmission(
            this.signupDetail,
            this.kycDetail,
            this.addressDetail,
            this.bankingDetails);

        this.publicService.signup(this.signupForm).subscribe({
            next: (signUpResponse: SignupResponse) =>
            {
                this.stopSubmitSignupLoader();


                // the user has successfully signed up their business. Redirect to the login page.
                this.checkAndShowSuccessPopup(signUpResponse?.verificationUrl);
            },
            error: error =>
            {
                this.stopSubmitSignupLoader();

                if (error.status === HttpStatusCode.NotFound)
                {
                    this.alertService.error(
                        'There is currently a system configuration issue preventing new sign-ups. ' +
                        'Please inform the administrators for them to resolve it.');
                }
                else
                {
                    this.alertService.error(error);
                }
            }
        });
    }

    private startSubmitSignupLoader(message: string)
    {
        this.loaderText = message;
        this.loaderService.start();
    }

    private stopSubmitSignupLoader()
    {
        this.loaderText = 'Loading ...';
        this.loaderService.stop();
    }

    private checkAndShowSuccessPopup(selfieVerificationUrl: string | null | undefined): void
    {
        if (this.getIsIntegratorSignup())
        {
            this.navigateToLogin();
            return;
        }

        const isJuristicWithRegistrationNumber = this.signupDetail.businessType !== BusinessType.SOLE_PROPRIETOR
            && !!this.kycDetail?.registrationNumber;

        if (!isJuristicWithRegistrationNumber)
        {
            this.showSuccessPopup(selfieVerificationUrl);
            this.navigateToLogin();
            return;
        }

        const registrationNumberValidation = {
            registrationNumber: this.kycDetail.registrationNumber?.replace(/\s+/g, ''),
            integratorPrefix: this.integratorSignupDetails?.integratorPrefix,
            businessType: this.signupDetail.businessType
        };

        this.publicService.validateRegistrationNumberExists(registrationNumberValidation).subscribe({
            next: (response: Response) =>
            {
                if (response.responseCode === ResponseEnum.FOUND)
                {
                    this.showSuccessPopup(selfieVerificationUrl);
                }
                this.navigateToLogin();
            },
            error: () =>
            {
                this.showSuccessPopup(selfieVerificationUrl);
                this.navigateToLogin();
            }
        });
    }

    private showSuccessPopup(selfieVerificationUrl: string | null | undefined): void
    {
        const trimmedUrl = selfieVerificationUrl?.trim();
        const hasSelfieUrl = !!trimmedUrl;

        this.alertService.success(
            this.buildSignupSuccessMessage(hasSelfieUrl),
            true,
            this.environmentService.signupSuccessModalTag,
            hasSelfieUrl ? {
                type: 'link',
                url: trimmedUrl,
                label: 'Verify Your Identity',
                cssClass: 'selfie-verify-btn'
            } : undefined);
    }

    /**
     * Builds the HTML message shown in the signup success popup. When identity verification is ready, the body explains
     * that verification can be completed via the footer button or the emailed link; otherwise the original email-based
     * message is returned unchanged. The selfie button itself is rendered in the modal footer, not in this body.
     */
    private buildSignupSuccessMessage(hasSelfieUrl: boolean): string
    {
        if (hasSelfieUrl)
        {
            return `Thank you for signing up!
                 <br/>
                 <br/>
                 <strong>Your identity verification is ready - you can complete it now using the button below, or through the link emailed to you.
                 It's only valid for 24 hours.</strong>
                 <br/>
                 <br/>
                 If you miss this 24-hour window, you will have to start again with a new sign-up.
                 <br>
                 <br>
                 <strong>Tip: Please only do one business sign-up at a time!</strong>`;
        }

        return `Thank you for signing up!
                 <br/>
                 <br/>
                 <strong>Should your application be successful, you will receive an email for your identity verification link - it's only valid for 24
                  hours.</strong>
                 <br/>
                 <br/>
                 If you miss this 24-hour window, you will have to start again with a new sign-up.
                 <br>
                 <br>
                 <strong>Tip: Please only do one business sign-up at a time!</strong>`;
    }

    private showCheckEmailPopup(): void
    {
        this.alertService.success(
            `<strong>Almost done!</strong> To finish creating your account, please check your inbox for the
                 confirmation link.
                 <br/>
                 <br/>
                 You've registered, and we've sent a confirmation email to <strong>${this.signupDetail?.email}</strong>.
                 <br/>
                 <span class="deadline-warning">Hurry - it expires in 24 hours!</span>
                 <br/>
                 <br/>
                 <i>*If you don't see the email, please check your spam folder.</i>`,
            true,
            this.environmentService.signupSuccessModalTag);
    }

    private retrieveIntegratorSignupDetails(): void
    {
        this.route.paramMap.subscribe(params =>
        {
            const merchantIdentifier: string = params.get('merchantIdentifier');
            const integratorPrefix: string = params.get('integratorPrefix');
            const integratorUuid: string = params.get('integratorUuid');

            // Integrator signup is considered "true" if only integratorUuid is present
            // and merchantIdentifier/integratorPrefix are absent.
            const isIntegratorSignup: boolean = !!integratorUuid && !merchantIdentifier && !integratorPrefix;

            this.integratorSignupDetails = {
                merchantIdentifier,
                integratorPrefix,
                integratorUuid,
                isIntegratorSignup: isIntegratorSignup
            };

            this.retrieveIntegratorDetails();
        });
    }

    private loadPrefilledSignupDetails()
    {
        const merchantIdentifier = this.integratorSignupDetails?.merchantIdentifier ?? null;
        const integratorPrefix = this.integratorSignupDetails?.integratorPrefix ?? null;
        const integratorUuid = this.integratorSignupDetails?.integratorUuid ?? null;

        if (merchantIdentifier && integratorPrefix && integratorUuid)
        {
            this.publicService.getPrefilledSignupDetailsForMerchant(
                merchantIdentifier,
                integratorPrefix,
                integratorUuid).subscribe({
                next: (signup: Signup) =>
                {
                    const password: string = this.getIsMerchantSelfServiceEnabled() || this.getIsIntegratorSignup() || signup.password ? signup.password : PasswordGenerator.generatePassword();
                    this.signupDetail = {
                        businessDisplayName: signup.businessDisplayName ?? null,
                        businessName: signup.businessName ?? null,
                        clientBase: signup.clientBase ?? null,
                        averageTransactionValue: signup.averageTransactionValue ?? null,
                        businessType: signup.businessType ?? BusinessType.SOLE_PROPRIETOR,
                        name: signup.name ?? null,
                        surname: signup.surname ?? null,
                        mobileNumber: signup.mobileNumber ?? null,
                        email: signup.email ?? null,
                        password: password,
                        marketingConsent: false,
                        dateTermsAccepted: new Date()
                    };

                    this.kycDetail = {
                        registrationNumber: signup.registrationNumber ?? null,
                        directorName: signup.directorName ?? null,
                        directorSurname: signup.directorSurname ?? null,
                        directorIdNumber: signup.directorIdNumber ?? null,
                        fspNumber: signup.fspNumber ?? null,
                        natureOfBusiness: signup.natureOfBusiness ?? null,
                        overview: signup.overview ?? null,
                        websiteUrl: signup.websiteUrl ?? null,
                        token: null
                    };

                    this.addressDetail = {
                        address1: signup.address1 ?? null,
                        address2: signup.address2 ?? null,
                        city: signup.city ?? null,
                        province: signup.province ?? null,
                        postalCode: signup.postalCode ?? null,
                        billingEmailAddress: signup.billingEmail ?? null,
                    };

                    this.bankingDetails = {
                        bankName: signup.bankName ?? null,
                        bankAccountNumber: signup.bankAccountNumber ?? null,
                        bankBranch: signup.bankBranch ?? null
                    };

                }, error: (error) =>
                {
                    if (error.status === HttpStatusCode.NotFound)
                    {
                        this.handleInvalidSignupLink();
                    }
                    else
                    {
                        this.alertService.error('An error occurred while loading prefilled signup data.');
                    }
                }
            });
        }
    }

    private handleInvalidSignupLink(): void
    {
        this.alertService.error(
            'This signup link is invalid or no longer available. Please contact your integrator if you need a new link.',
            true);
        this.navigateToLogin();
    }

    private retrieveIntegratorDetails(): void
    {
        const integratorUuid = this.integratorSignupDetails?.integratorUuid;

        this.loaderService.start();

        const integrator$ = integratorUuid
            ? this.publicService.getIntegratorByUuid(integratorUuid)
            : this.publicService.getDefaultIntegrator();

        integrator$
            .pipe(finalize(() => this.loaderService.stop()))
            .subscribe({
                next: details => this.merchantIntegratorDetails = details,
                error: err => this.handleIntegratorError(integratorUuid, err)
            });
    }

    private handleIntegratorError(integratorUuid: string | undefined, error: any): void
    {
        console.debug(error);

        if (integratorUuid)
        {
            this.alertService.error(
                'The signup link is invalid. Please check the URL and try again. If the issue continues, please' +
                ' contact support for assistance.',
                true);
        }
        else
        {
            this.alertService.error(
                'There is currently a system configuration issue preventing new sign-ups. ' +
                'Please contact support for assistance.', true);
        }

        this.navigateToLogin();
    }

    private navigateToLogin(): void
    {
        this.router.navigate([ROUTES.PUBLIC.LOGIN.path]).then();
    }
}